import { useEffect, useMemo, useRef, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { SelectOption } from "@/components/ui/select"
import { PolicyListRow } from "@/components/domain/policy-list-row"
import { PolicyEditorPanel } from "@/components/domain/policy-editor-panel"
import { PolicyDiffBanner } from "@/components/domain/policy-diff-banner"
import { EscalationPolicyDesignerPane } from "@/components/domain/escalation-policy-designer-pane"
import { VersionTimeline } from "@/components/domain/version-timeline"
import type {
  EscalationPoliciesIndexProps,
  EscalationPolicy,
} from "@/types/escalation_policy"

// /escalation_policies — two-column list+editor layout for Auto Triage Policies.
//
// Left column: filterable list of rules (PolicyListRow). First-match-wins
// ordering communicated by drag-handle + position. Selected row gets a red
// ring + tinted background.
//
// Right column: the focused rule's editor (PolicyEditorPanel). Local-state
// buffer; Cancel reverts; Save PATCHes. Toggle/delete also flow through
// the same use-case-style handlers on this page so list-row controls stay
// in sync.
//
// State model:
//   - The presenter sends `activeSet` = whatever the operator is currently
//     viewing (live or draft). `policies` is already that set's rows.
//   - When a draft exists, the user can flip between live + draft via the
//     "Switch to live/draft" link in the banner. We re-fetch via Inertia
//     (?view=...) so the server can return the right slice.
//   - All mutations target the draft. Editor controls are gated on
//     `editable = activeSet.state === "draft"`.

const BASE = "/escalation_policies"

const STATUS_FILTER_OPTIONS: SelectOption<string>[] = [
  { value: "active",   label: "Active rules" },
  { value: "archived", label: "Show archived" },
  { value: "all",      label: "All rules" },
]

export default function EscalationPoliciesIndex({
  activeSet,
  liveSet,
  draftSet,
  lineage,
  policies,
  diff,
  users,
  agents,
  reasonOptions,
  topicOptions,
  agentPools,
  ticketQueues,
  policyCapabilities,
}: EscalationPoliciesIndexProps) {
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<"active" | "archived" | "all">("active")
  const [designerOpen, setDesignerOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(policies[0]?.id ?? null)

  // Track previous policy ids so we can auto-select a freshly-added rule.
  const prevIdsRef = useRef<Set<number>>(new Set(policies.map((p) => p.id)))

  // After Inertia reloads (props change): if the selected id is gone,
  // fall back to the first row. If a brand-new id appeared, prefer it.
  useEffect(() => {
    const ids = new Set(policies.map((p) => p.id))
    const newlyAdded = policies.find((p) => !prevIdsRef.current.has(p.id))
    if (newlyAdded) {
      setSelectedId(newlyAdded.id)
    } else if (selectedId == null || !ids.has(selectedId)) {
      setSelectedId(policies[0]?.id ?? null)
    }
    prevIdsRef.current = ids
  }, [policies]) // eslint-disable-line react-hooks/exhaustive-deps

  const editable = activeSet?.state === "draft"
  const viewing: "live" | "draft" = activeSet?.state === "draft" ? "draft" : "live"

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return policies.filter((p) => {
      if (term && !p.name.toLowerCase().includes(term)) return false
      if (statusFilter === "active"   && !p.active) return false
      if (statusFilter === "archived" &&  p.active) return false
      return true
    })
  }, [policies, search, statusFilter])

  const selectedPolicy = useMemo(
    () => policies.find((p) => p.id === selectedId) ?? null,
    [policies, selectedId],
  )

  const selectedRuleNumber = useMemo(() => {
    if (!selectedPolicy) return 0
    const idx = policies.findIndex((p) => p.id === selectedPolicy.id)
    return idx >= 0 ? idx + 1 : selectedPolicy.position + 1
  }, [policies, selectedPolicy])

  // -- mutations -----------------------------------------------------------

  const updatePolicy = (id: number, body: Partial<EscalationPolicy>) => {
    router.patch(
      `${BASE}/${id}`,
      { escalation_policy: body } as unknown as Record<string, never>,
      { preserveScroll: true },
    )
  }
  const deletePolicy = (id: number) => {
    // Pick a fallback row to focus once the active one disappears.
    const idx = policies.findIndex((p) => p.id === id)
    const next = policies[idx + 1] ?? policies[idx - 1] ?? null
    setSelectedId(next?.id ?? null)
    router.delete(`${BASE}/${id}`, { preserveScroll: true })
  }
  const togglePolicyActive = (id: number, active: boolean) => {
    updatePolicy(id, { active })
  }
  const restorePolicy = (id: number) => {
    updatePolicy(id, { active: true })
  }
  const addRule = () => {
    router.post(BASE, {
      escalation_policy: {
        name:     "New rule",
        position: policies.length,
        active:   true,
      },
    }, { preserveScroll: true })
  }
  // Drag-drop reorder: caller passes the policy id being dragged and the
  // index it was dropped on. We reorder the FULL list (not the filtered
  // view) so an active-rules-only filter doesn't accidentally reorder
  // archived rows. POST returns 200 + the controller refreshes via
  // Inertia, so the new positions show up on the next render.
  const reorderPolicies = (fromId: number, toIndex: number) => {
    if (!editable) return
    const fullOrder = policies.map((p) => p.id)
    const fromIdx = fullOrder.indexOf(fromId)
    if (fromIdx < 0 || fromIdx === toIndex) return
    const next = [...fullOrder]
    next.splice(fromIdx, 1)
    next.splice(toIndex, 0, fromId)
    router.post(`${BASE}/reorder`, { ids: next }, {
      preserveScroll: true,
      preserveState:  true,
    })
  }
  const switchView = (next: "live" | "draft") => {
    router.get(BASE, { view: next }, { preserveScroll: true, preserveState: false })
  }
  // Click an archived dot → load THAT version of the set in read-only
  // mode. Versioning is managed at the SET level, so we route by version
  // number, mirroring how Skills handle it.
  const selectVersion = (setId: number) => {
    const target = lineage.find((v) => v.id === setId)
    if (!target) return
    router.get(BASE, { version: target.version }, { preserveScroll: true, preserveState: false })
  }

  // -- render --------------------------------------------------------------

  return (
    <AppShell>
      {/* Custom page header — denser than the global PageHeader to match the mockup. */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-app-muted">Settings</div>
          <h1 className="text-xl font-semibold text-app-text">Auto Triage Policies</h1>
          <p className="mt-1 max-w-2xl text-xs text-app-muted">
            Define rules that automatically route escalations. Rules are evaluated top-to-bottom and the first match wins.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rules…"
            className="w-56"
          />
          <div className="min-w-[160px]">
            <Select<string>
              value={STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter) ?? null}
              onChange={(o) => o && setStatusFilter(o.value as typeof statusFilter)}
              options={STATUS_FILTER_OPTIONS}
              isSearchable={false}
            />
          </div>
          <Button
            variant="red"
            size="md"
            onClick={addRule}
            disabled={!policyCapabilities.canEdit}
            title={
              !policyCapabilities.canEdit ? "Editing requires agent_manager or admin"
              : editable             ? "Add a new rule to the draft"
                                     : "Adding a rule will start a draft from the current live set"
            }
          >
            + Add rule
          </Button>
        </div>
      </div>

      {lineage.length > 1 && (
        <Card className="mb-3 px-3 py-2">
          <VersionTimeline
            versions={lineage}
            selectedVersionId={activeSet.id}
            onSelect={selectVersion}
          />
        </Card>
      )}

      <PolicyDiffBanner
        liveSet={liveSet}
        draftSet={draftSet}
        activeSet={activeSet}
        diff={diff}
        policyCapabilities={policyCapabilities}
        viewing={viewing}
        onSwitchView={switchView}
        onOpenDesigner={() => setDesignerOpen(true)}
      />

      {/* Two-column list+editor */}
      <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)]">
        {/* Left: rule list */}
        <Card className="flex flex-col p-0">
          <div className="border-b border-border px-4 py-3 text-sm font-semibold text-app-text">
            Rules <span className="text-app-muted">({filtered.length})</span>
          </div>

          {filtered.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-app-muted">
              {policies.length === 0 ? (
                <>
                  <div>
                    No triage rules yet. Click{" "}
                    <span className="font-medium text-app-text">+ Add rule</span>{" "}
                    or ask Forge to set things up.
                  </div>
                  <div className="mt-2 text-xs text-app-subtle">
                    Tip: define your operator groups under{" "}
                    <Link href="/agent_pools" className="text-brand-red hover:underline">
                      Agent Pools
                    </Link>{" "}
                    first, then reference them here.
                  </div>
                </>
              ) : (
                <>No rules match your filters.</>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {filtered.map((p) => (
                <PolicyListRow
                  key={p.id}
                  policy={p}
                  users={users}
                  agents={agents}
                  agentPools={agentPools}
                  ticketQueues={ticketQueues}
                  editable={editable}
                  selected={p.id === selectedId}
                  fullIndex={policies.findIndex((x) => x.id === p.id)}
                  onSelect={setSelectedId}
                  onDelete={deletePolicy}
                  onToggleActive={togglePolicyActive}
                  onRestore={restorePolicy}
                  onReorder={reorderPolicies}
                />
              ))}
            </div>
          )}

          {filtered.length > 0 && (
            <div className="border-t border-border px-4 py-2 text-[11px] italic text-app-subtle">
              Drag to reorder rules. The first matching rule is applied.
            </div>
          )}
        </Card>

        {/* Right: editor panel */}
        <Card className="p-0">
          {selectedPolicy ? (
            <PolicyEditorPanel
              key={selectedPolicy.id}
              policy={selectedPolicy}
              users={users}
              agents={agents}
              reasonOptions={reasonOptions}
              topicOptions={topicOptions}
              agentPools={agentPools}
              ticketQueues={ticketQueues}
              editable={editable}
              ruleNumber={selectedRuleNumber}
              onSave={updatePolicy}
              onDelete={deletePolicy}
              onToggleActive={togglePolicyActive}
            />
          ) : (
            <div className="flex h-full min-h-[320px] items-center justify-center px-6 py-10 text-center text-sm text-app-muted">
              Select a rule on the left, or click{" "}
              <span className="mx-1 font-medium text-app-text">+ Add rule</span>{" "}
              to create one.
            </div>
          )}
        </Card>
      </div>

      {designerOpen && (
        <EscalationPolicyDesignerPane
          isDraft={editable}
          onClose={() => setDesignerOpen(false)}
          policies={policies}
          users={users}
          agents={agents}
          agentPools={agentPools}
          reasonOptions={reasonOptions}
          topicOptions={topicOptions}
        />
      )}
    </AppShell>
  )
}
